import React, { useState, useEffect, useRef } from "react";
import { startProcessInstance, executeWorkflowStep } from "../../../../services/api/Api";
import { useIntl } from "react-intl";
import projectConfig from "@projectConfig";
import { getForm } from "../../../../services/restAPI/endpoints/Forms";

export const useExcelExport = () => {
   const [isExporting, setIsExporting] = useState(false);
   const [exportStatus, setExportStatus] = useState("idle");
   const [error, setError] = useState(null);
   const isMountedRef = useRef(true);

   // Permanent process configuration ID for Excel export workflow
   // This ID corresponds to the backend process definition for list data export
   const EXCEL_EXPORT_CONFIG_ID = projectConfig.listExportWorkflowConfigId;

   const intl = useIntl();

   useEffect(() => {
      return () => {
         isMountedRef.current = false;
      };
   }, []);

   const initiateExport = async (listId) => {
      if (!EXCEL_EXPORT_CONFIG_ID) {
         const errorMsg = intl.formatMessage({ id: "excelExport.unsupported" });
         setError(errorMsg);
         setExportStatus("error");
         throw new Error(errorMsg);
      }

      if (!listId) {
         const errorMsg = intl.formatMessage({ id: "excelExport.noListId" });
         setError(errorMsg);
         setExportStatus("error");
         throw new Error(errorMsg);
      }

      setIsExporting(true);
      setExportStatus("processing");
      setError(null);

      // this prevents an api timing issue that was detected during development
      let processDataTemp = {};

      try {
         if (!isMountedRef.current) return;

         let step1Response;
         try {
            step1Response = await startProcessInstance(EXCEL_EXPORT_CONFIG_ID);
            if (!isMountedRef.current) return;

            let responseData = step1Response.data;
            if (typeof responseData === "string") {
               responseData = JSON.parse(responseData.substring(6));
            }

            step1Response.data = responseData;
         } catch (step1Error) {
            throw step1Error;
         }

         if (!step1Response?.data?.start) {
            throw new Error(intl.formatMessage({ id: "excelExport.missingStartData" }));
         }

         const formId = step1Response.data.start.formId;
         const instanceId = step1Response.data.start.instanceId;
         const stepId = step1Response.data.start.stepId;
         const actionKey = step1Response.data.start.actions?.[0]?.actionId;

         if (!formId || !instanceId || !stepId || !actionKey) {
            throw new Error(intl.formatMessage({ id: "excelExport.missingRequiredFields" }));
         }

         processDataTemp = {
            instanceId: instanceId,
            stepId: stepId,
            formId: formId,
            actionKey: actionKey,
         };

         // get form data
         if (!isMountedRef.current) return;

         const formResponse = await getForm(formId);
         if (!isMountedRef.current) return;

         if (!formResponse?.data?.default?.mainForm?.forms_data?.tab?.[0]?.elements) {
            throw new Error(intl.formatMessage({ id: "excelExport.invalidFormDataStructure" }));
         }

         // create the map
         const elements = formResponse.data.default.mainForm.forms_data.tab[0].elements;
         const newFormDataMap = {};

         Object.values(elements).forEach((element) => {
            const techName = element.object.UIP_TECH_NAZWA;
            const value = element.value;
            newFormDataMap[techName] = value;
         });

         // add values
         newFormDataMap.list_id = listId;
         newFormDataMap.easyForm = true;
         newFormDataMap._formKey = formResponse.data.default.mainForm._formKey;
         newFormDataMap.UIF_ID = processDataTemp.formId;

         // execution
         const finalResponse = await executeWorkflowStep(
            processDataTemp.actionKey,
            newFormDataMap,
            null, // comment
            null, // recipientId
            processDataTemp.stepId,
         );

         if (!isMountedRef.current) return;

         setExportStatus("success");
         setIsExporting(false);
         return finalResponse.data;
      } catch (error) {
         if (isMountedRef.current) {
            setExportStatus("error");
            setError(error);
            setIsExporting(false);
         }
         throw error;
      }
   };

   const resetExport = () => {
      if (!isMountedRef.current) return;
      setIsExporting(false);
      setExportStatus("idle");
      setError(null);
   };

   return {
      // State
      isExporting,
      exportStatus,
      error,
      // Methods
      initiateExport,
      resetExport,
      // Status helpers
      isIdle: exportStatus === "idle",
      isProcessing: exportStatus === "processing",
      isSuccess: exportStatus === "success",
      isError: exportStatus === "error",
      isSupported: !!EXCEL_EXPORT_CONFIG_ID,
   };
};
